// Main facade — pure functions that orchestrate the export flow
// This module imports and re-exports all the helper functions for export, so that the main exportGrid function can be clean and focused on orchestration.

import { validateBeforeExport } from './validate.js';
import { cloneColumnsDeep } from './cloneColumns.js';
import { flattenColumns } from './flattenColumns.js';
import { syncVisibility } from './syncVisibility.js';
import { processExportFields } from './processExportFields.js';
import { exportAsPdf } from './pdfExporter.js';
import { exportAsExcel } from './excelExporter.js';
import { formatCellValue } from './cellFormatter.js';


export async function exportGrid(e, fileName, originalColumns, options = {}) {
    if (!validateBeforeExport(e, options)) return;

    // e.component is the dxDataGrid instance
    const grid = e.component;

    // compute visible flat columns
    const visibleFlat = flattenColumns(options.visibleColumns || grid.getVisibleColumns());

    // Clone for export — never mutate originalColumns directly (it's a reactive computed reference)
    let columnsForExport = cloneColumnsDeep(originalColumns);
    syncVisibility(columnsForExport, visibleFlat);

    // process export fields (may reveal hidden fields for export)
    const columnNames = [];
    columnsForExport = processExportFields(columnsForExport, columnNames, options.persistenceKey, options.storageGetter);

    const finalColumnsForExport = columnsForExport.filter((c) => c.caption || c.columns);

    // Build a visibility map (dataField → visible) so exportAsExcel/exportAsPdf can apply it
    // via customizeColumns without mutating the live grid — avoids server re-fetch on server-paged grids.
    const flatFinalColumns = flattenColumns(finalColumnsForExport);
    const exportVisibilityMap = new Map(
        flatFinalColumns
            .filter((col) => col.dataField)
            .map((col) => [col.name, col.visible !== false])
    );


    try {
        if (e.format === 'pdf') {
            await exportAsPdf({ e, fileName, grid, finalColumnsForExport, formatCellValue, options, exportVisibilityMap });
        } else {
            await exportAsExcel({ e, fileName, grid, finalColumnsForExport, formatCellValue, options, exportVisibilityMap });
        }
    }
    catch (err) {
        // eslint-disable-next-line no-console
        console.error("Error during export:", err);
    }
}